import React, {useState, useRef, useEffect, useCallback} from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import Skeleton from "connectui/component/display/Skeleton";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import Container from "connectui/component/layout/Container";
import ButtonText from "connectui/component/input/button/ButtonText";
import {Waypoint} from "react-waypoint";

import ListComponent from "connectui/component/layout/list/ListComponent";
import {fetchGuestLogs} from "redux/actions/guestLogsActions";
import {useDispatch, useSelector} from "react-redux";
import {format} from "date-fns";
import LogItem from "./LogItem";
import {useTranslation} from "react-i18next";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      padding: theme.spacing(5, 4, 0, 4),
      position: "relative",
      backgroundColor: theme.colors.gray50,
      display: "flex",
      flexDirection: "column",
    },
    buttonWrapper: {
      position: "absolute",
      top: theme.spacing(2),
      right: theme.spacing(2),
      zIndex: 10,
    },
    expandAllButton: {
      backgroundColor: theme.colors.white,
      boxShadow: theme.shadows.xs,
      opacity: 0.6,
      transition: "opacity 0.2s, background-color 0.2s",
      padding: theme.spacing(1, 2),
      border: "none",
      "&:hover": {
        opacity: 1,
        backgroundColor: theme.colors.gray50,
      },
    },
    logsContainer: {
      width: "100%",
      padding: theme.spacing(2, 0, 0, 0),
      flex: 1,
      overflow: "auto",
      "&::-webkit-scrollbar": {width: "0!important"},
    },
    logsList: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(4),
      paddingTop: theme.spacing(2),
    },
    loadingMore: {
      width: "100%",
      textAlign: "center",
      padding: theme.spacing(2, 0),
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    emptyContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
    },
  });

/**
 * Panel component to display guest audit logs
 */
const GuestLogsPanel = ({logs = [], skeleton = {enabled: false}, guestId}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const [expandAll, setExpandAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef(null);
  const dispatch = useDispatch();

  const guestLogsReducer = useSelector(
    (state) => state.defaultReducer.guest_logs,
  );
  const storedLogs = guestLogsReducer.logs || [];
  const hasMoreStoredLogs = guestLogsReducer.hasMore || false;
  const lastStoredItem = guestLogsReducer.lastItem || null;
  const isLoading = guestLogsReducer.loading || false;

  useEffect(() => {
    setDisplayedLogs([]);
    setCurrentPage(1);
    setHasMore(true);
    setLoadingMore(false);
    setExpandedRows({});
    setExpandAll(false);

    if (guestId && !skeleton.enabled) {
      loadLogs(1, true);
    }
  }, [guestId, skeleton.enabled]);

  const loadLogs = useCallback(
    (page = 1, shouldReset = false) => {
      if ((!hasMore && page > 1) || !guestId || skeleton.enabled) return;

      setLoadingMore(true);
      if (shouldReset) {
        setDisplayedLogs([]);
      }

      const params = {
        guestId,
        append: page > 1,
      };

      if (page > 1 && lastStoredItem) {
        params.startTimestamp = lastStoredItem.timestamp;
      }

      dispatch(fetchGuestLogs(params))
        .then((result) => {
          setDisplayedLogs((prev) =>
            shouldReset ? result.logs : [...prev, ...result.logs],
          );
          setCurrentPage(page);
          setHasMore(result.hasMore);
          setLoadingMore(false);
        })
        .catch((error) => {
          console.error("Error loading logs:", error);
          setLoadingMore(false);
        });
    },
    [guestId, dispatch, lastStoredItem, hasMore, skeleton.enabled],
  );

  useEffect(() => {
    if (storedLogs.length > 0) {
      setDisplayedLogs(storedLogs);
      setHasMore(hasMoreStoredLogs);
      setLoadingMore(isLoading);
    }
  }, [storedLogs, hasMoreStoredLogs, isLoading]);

  const loadMoreLogs = useCallback(() => {
    if (loadingMore || !hasMore || skeleton.enabled) return;
    loadLogs(currentPage + 1);
  }, [currentPage, loadingMore, hasMore, skeleton.enabled, loadLogs]);

  const handleRowExpandToggle = useCallback((index, isExpanded) => {
    setExpandedRows((prev) => {
      const newState = {...prev};
      if (isExpanded) {
        newState[index] = true;
      } else {
        delete newState[index];
      }
      return newState;
    });
  }, []);

  useEffect(() => {
    const newExpandedRows = {};

    if (expandAll) {
      displayedLogs.forEach((_, index) => {
        newExpandedRows[index] = true;
      });
    }

    setExpandedRows(newExpandedRows);
  }, [expandAll, displayedLogs]);

  const getLogItemClass = (type) => {
    const bgColorMap = {
      warning: theme.colors.warning50,
      error: theme.colors.error50,
      info: theme.colors.white,
    };

    return {
      ...classes.logItem,
      backgroundColor: bgColorMap[type] || theme.colors.white,
    };
  };

  if (skeleton.enabled) {
    return (
      <Container className={classes.container}>
        <Container className={classes.logsContainer}>
          <ListComponent
            gap={theme.spacing(4)}
            listStyles={{padding: theme.spacing(2, 4)}}
          >
            {Array.from({length: 50}).map((_, index) => (
              <Container key={`skeleton-${index}`}>
                <Skeleton
                  width="100%"
                  height="80px"
                  style={{
                    marginBottom: theme.spacing(2),
                  }}
                />
              </Container>
            ))}
          </ListComponent>
        </Container>
      </Container>
    );
  }

  if (displayedLogs.length === 0 && !loadingMore) {
    return (
      <Container className={classes.container}>
        <Container className={classes.emptyContainer}>
          <Text variant="textLG" color="gray400">
            {t("inbox.guest-logs.list.no-logs-found")}
          </Text>
        </Container>
      </Container>
    );
  }

  return (
    <Container className={classes.container}>
      {displayedLogs.length > 0 && (
        <Container className={classes.buttonWrapper}>
          <ButtonText
            variant="textXS"
            label={
              expandAll
                ? t("inbox.guest-logs.list.collapse-all")
                : t("inbox.guest-logs.list.expand-all")
            }
            color="primary"
            onClick={() => setExpandAll(!expandAll)}
            icon={
              <SvgIcon
                component={
                  expandAll ? ICONS["chevron-up"] : ICONS["chevron-down"]
                }
                sx={{width: "8px", height: "8px"}}
              />
            }
            className={classes.expandAllButton}
          />
        </Container>
      )}

      <Container className={classes.logsContainer} ref={containerRef}>
        <ListComponent
          gap={theme.spacing(4)}
          listStyles={{padding: theme.spacing(2, 4)}}
        >
          {displayedLogs.map((log, index) => (
            <Container
              key={log.id ? `log-${log.id}-${index}` : `log-${index}`}
              sx={{width: "100%"}}
            >
              <LogItem
                log={log}
                index={index}
                logItemClass={getLogItemClass(log.severity)}
                isExpanded={expandAll || !!expandedRows[index]}
                onExpandToggle={handleRowExpandToggle}
              />

              {hasMore && index === displayedLogs.length - 5 && (
                <Waypoint onEnter={loadMoreLogs} bottomOffset="-300px" />
              )}
            </Container>
          ))}

          {loadingMore && (
            <Container sx={{width: "100%"}}>
              <Skeleton width={"100%"} height={"50px"} />
            </Container>
          )}

          {!hasMore && displayedLogs.length > 0 && (
            <Container className={classes.loadingMore}>
              <Text variant="textSM" color="gray500">
                {t("inbox.guest-logs.list.no-more-logs")}
              </Text>
            </Container>
          )}
        </ListComponent>
      </Container>
    </Container>
  );
};

export default GuestLogsPanel;
